/**
 * Bank accounts tab: whether the system check may give a first signature, and our own company's
 * accounts (the account's main party), managed like a partner's.
 */
import { useQuery } from '@apollo/client/react';
import { Paper, Toggle } from '@insio/ui-toolkit';
import { Permission } from '@insio/shared-types';
import { GetProfileDocument } from '@/graphql';
import { usePermissions } from '@/features/auth';
import { PartyBankAccountsPanel, usePaymentSettings } from '@/features/party-bank-accounts';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useToastHelpers } from '@/hooks/use-toast-helpers';

export function BankAccountsSettings() {
  const t = useFeatureTranslation('systemSettings.bankAccounts');
  const { hasPermission } = usePermissions();
  const { showSuccess, showError } = useToastHelpers();
  const settings = usePaymentSettings();
  // Anyone reads the switch; changing it is ACCOUNT_SETTINGS, as the backend guards it.
  const canChange = hasPermission(Permission.ACCOUNT_SETTINGS);

  const { data: profile } = useQuery(GetProfileDocument, { fetchPolicy: 'cache-first' });
  const mainParty = profile?.profile?.account?.mainParty ?? null;

  const toggleSystemCheck = async (enabled: boolean) => {
    try {
      await settings.setSystemCheckEnabled(enabled);
      showSuccess(t('systemCheck.saved'));
    } catch {
      showError(t('systemCheck.saveError'));
    }
  };

  return (
    <div className='space-y-md'>
      <Paper showDivider={false} className='px-0 sm:px-lg' testId='bank-account-system-check'>
        <Paper.Content>
          <div className='flex flex-col gap-sm'>
            <h2 className='text-base font-semibold text-foreground-base'>
              {t('systemCheck.title')}
            </h2>
            <p className='text-sm text-foreground-muted'>{t('systemCheck.description')}</p>
            {settings.error && !settings.loaded ? (
              <p role='alert' className='text-sm text-error-text'>
                {t('systemCheck.loadError')}
              </p>
            ) : (
              <Toggle
                labelText={t('systemCheck.label')}
                checked={settings.isSystemCheckEnabled}
                disabled={!canChange || !settings.loaded || settings.saving}
                onChange={enabled => void toggleSystemCheck(enabled)}
                testId='bank-account-system-check-toggle'
              />
            )}
            {!canChange && (
              <p className='text-xs text-foreground-muted'>{t('systemCheck.noPermission')}</p>
            )}
          </div>
        </Paper.Content>
      </Paper>

      <Paper showDivider={false} className='px-0 sm:px-lg' testId='own-bank-accounts'>
        <Paper.Content>
          <div className='flex flex-col gap-sm'>
            <h2 className='text-base font-semibold text-foreground-base'>
              {t('ownAccounts.title')}
            </h2>
            {mainParty ? (
              <PartyBankAccountsPanel partyId={mainParty.id} partyName={mainParty.name} />
            ) : (
              profile && (
                <p className='text-sm text-foreground-muted'>{t('ownAccounts.noCompany')}</p>
              )
            )}
          </div>
        </Paper.Content>
      </Paper>
    </div>
  );
}
